import api from './api';
import { API_ROUTES } from '../config/api';

export interface CorrectionOperation {
  type: 'add' | 'update' | 'delete';
  date: string;
  sales?: number;
  article: string;
}

export class CorrectionsService {
  private static instance: CorrectionsService;
  private pendingOperations: Map<string, CorrectionOperation> = new Map();
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): CorrectionsService {
    if (!CorrectionsService.instance) {
      CorrectionsService.instance = new CorrectionsService();
    }
    return CorrectionsService.instance;
  }

  // Get cached data if still valid
  private getCachedData(key: string): any | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  // Set cached data
  private setCachedData(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  // Clear cache for specific article
  private clearCacheForArticle(articleId: string): void {
    const keysToDelete = Array.from(this.cache.keys()).filter(key => key.includes(articleId));
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  // Clear all corrections cache for a database
  private clearAllCorrectionsCache(databaseId: number | string): void {
    const cacheKey = `corrections-all-${databaseId}`;
    this.cache.delete(cacheKey);
  }

  // Add or update a correction
  async saveCorrection(
    databaseId: number | string, 
    articleId: string, 
    date: string, 
    sales: number,
    onSuccess?: (date: string) => void,
    onError?: (date: string, error: any) => void
  ): Promise<void> {
    const operationKey = `${date}-${articleId}`;
    
    try {
      await api.post(
        API_ROUTES.PREDICTION.BASE + API_ROUTES.PREDICTION.CORRECTED_DATA(databaseId, articleId),
        { date, sales, article: articleId }
      );

      // Remove from pending operations
      this.pendingOperations.delete(operationKey);
      
      // Clear cache for this article since data has changed
      this.clearCacheForArticle(articleId);
      
      // Clear all corrections cache for the database
      this.clearAllCorrectionsCache(databaseId);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(date);
      }
    } catch (error) {
      // Call error callback
      if (onError) {
        onError(date, error);
      }
      throw error;
    }
  }

  // Delete a correction
  async deleteCorrection(
    databaseId: number | string, 
    articleId: string, 
    date: string,
    onSuccess?: (date: string) => void,
    onError?: (date: string, error: any) => void
  ): Promise<void> {
    const operationKey = `${date}-${articleId}`;
    
    try {
      await api.delete(
        API_ROUTES.PREDICTION.BASE + API_ROUTES.PREDICTION.CORRECTED_DATA(databaseId, articleId),
        { data: { date } }
      );

      // Remove from pending operations
      this.pendingOperations.delete(operationKey);
      
      // Clear cache for this article since data has changed
      this.clearCacheForArticle(articleId);
      
      // Clear all corrections cache for the database
      this.clearAllCorrectionsCache(databaseId);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(date);
      }
    } catch (error) {
      // Call error callback
      if (onError) {
        onError(date, error);
      }
      throw error;
    }
  }

  // Batch operations for better performance
  async batchOperations(
    databaseId: number | string,
    operations: CorrectionOperation[],
    onSuccess?: (date: string) => void,
    onError?: (date: string, error: any) => void
  ): Promise<void> {
    // Group operations by type for better efficiency
    const addOperations = operations.filter(op => op.type === 'add' || op.type === 'update');
    const deleteOperations = operations.filter(op => op.type === 'delete');
    
    // Process add/update operations
    const addPromises = addOperations.map(operation => 
      this.saveCorrection(databaseId, operation.article, operation.date, operation.sales!, onSuccess, onError)
    );
    
    // Process delete operations
    const deletePromises = deleteOperations.map(operation => 
      this.deleteCorrection(databaseId, operation.article, operation.date, onSuccess, onError)
    );
    
    // Execute all operations concurrently
    await Promise.allSettled([...addPromises, ...deletePromises]);
    
    // Clear cache once after all operations
    if (operations.length > 0) {
      const articleId = operations[0].article;
      this.clearCacheForArticle(articleId);
      // Also clear all corrections cache for the database
      this.clearAllCorrectionsCache(databaseId);
    }
  }

  // Sync local corrections with backend and handle conflicts
  async syncCorrections(
    databaseId: number | string,
    articleId: string,
    localCorrections: any[],
    onConflict?: (localCorrection: any, backendCorrection: any) => void
  ): Promise<any[]> {
    try {
      // Get fresh data from backend
      const backendCorrections = await this.getCorrections(databaseId, articleId, true);
      
      // If getCorrections returned empty array due to error, return local corrections as fallback
      if (backendCorrections.length === 0 && localCorrections.length > 0) {
        return localCorrections;
      }
      
      // Create maps for easy comparison
      const localMap = new Map(localCorrections.map(c => [c.date, c]));
      const backendMap = new Map(backendCorrections.map(c => [c.date, c]));
      
      const conflicts: any[] = [];
      
      // Check for conflicts
      localMap.forEach((localCorrection, date) => {
        const backendCorrection = backendMap.get(date);
        if (backendCorrection && backendCorrection.sales !== localCorrection.sales) {
          // Conflict detected
          conflicts.push({ local: localCorrection, backend: backendCorrection });
          if (onConflict) {
            onConflict(localCorrection, backendCorrection);
          }
        }
      });
      
      // Return backend corrections (source of truth)
      return backendCorrections;
    } catch (error) {
      // On error, return local corrections as fallback
      return localCorrections;
    }
  }

  // Get pending operations count
  getPendingCount(): number {
    return this.pendingOperations.size;
  }

  // Clear pending operations
  clearPending(): void {
    this.pendingOperations.clear();
  }

  // Get corrections for an article with caching
  async getCorrections(
    databaseId: number | string,
    articleId: string,
    forceRefresh: boolean = false
  ): Promise<any[]> {
    const cacheKey = `corrections-${databaseId}-${articleId}`;
    
    // Return cached data if available and not forcing refresh
    if (!forceRefresh) {
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return cached;
      }
    }
    
    try {
      const response = await api.get(
        API_ROUTES.PREDICTION.BASE + API_ROUTES.PREDICTION.CORRECTED_DATA(databaseId, articleId)
      );
      
      const corrections = Array.isArray(response.data) ? response.data : [];
      
      // Cache the result
      this.setCachedData(cacheKey, corrections);
      
      return corrections;
    } catch (error) {
      console.error('Failed to get corrections:', error);
      return [];
    }
  }

  // Get ALL corrections for a database with caching
  async getAllCorrections(
    databaseId: number | string,
    forceRefresh: boolean = false
  ): Promise<any[]> {
    const cacheKey = `corrections-all-${databaseId}`;
    
    // Return cached data if available and not forcing refresh
    if (!forceRefresh) {
      const cached = this.getCachedData(cacheKey);
      if (cached) {
        return cached;
      }
    }
    
    try {
      const response = await api.get(
        API_ROUTES.PREDICTION.BASE + API_ROUTES.PREDICTION.ALL_CORRECTED_DATA(databaseId)
      );
      
      const corrections = Array.isArray(response.data) ? response.data : [];
      
      // Cache the result
      this.setCachedData(cacheKey, corrections);
      
      return corrections;
    } catch (error) {
      console.error('Failed to get all corrections:', error);
      return [];
    }
  }

  // Clear all cache
  clearAllCache(): void {
    this.cache.clear();
  }
}

export default CorrectionsService.getInstance(); 